/* ==========================================================================
   McCalls — Collection intro
   Clamps long collection descriptions — click the copy to expand / collapse
   (only wired when it actually overflows the clamp) — and smooth-scrolls the "Shop the
   range" jump to the product toolbar, sitting it flush beneath the sticky
   header (--header-offset, published by sticky-bars.js). Also wires the
   designer switcher on brand collections (← / → keys, hover prefetch).
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  function headerOffset() {
    var v = getComputedStyle(document.documentElement).getPropertyValue('--header-offset');
    var n = parseFloat(v);
    return isNaN(n) ? 0 : n;
  }

  function initIntro(wrap) {
    var copy = wrap.querySelector('[data-collection-intro-copy]');
    var jump = wrap.querySelector('[data-collection-intro-jump]');

    /* ── Click the copy to expand / collapse ──────────────────────────── */
    if (copy) {
      var expanded = false;
      var expandable = false;

      function setExpandable(on) {
        expandable = on;
        wrap.classList.toggle('is-expandable', on);
        if (on) {
          copy.setAttribute('role', 'button');
          copy.setAttribute('tabindex', '0');
          copy.setAttribute('aria-expanded', expanded ? 'true' : 'false');
          copy.setAttribute('title', expanded ? 'Click to show less' : 'Click to read more');
        } else {
          copy.removeAttribute('role');
          copy.removeAttribute('tabindex');
          copy.removeAttribute('aria-expanded');
          copy.removeAttribute('title');
        }
      }

      function measure() {
        if (expanded) return;
        wrap.classList.add('is-clamped');
        // Overflowing by more than a line-fraction → make the copy expandable.
        var overflows = copy.scrollHeight - copy.clientHeight > 4;
        if (!overflows) wrap.classList.remove('is-clamped');
        setExpandable(overflows);
      }

      function setExpanded(next) {
        expanded = next;
        wrap.classList.toggle('is-clamped', !next);
        copy.setAttribute('aria-expanded', next ? 'true' : 'false');
        copy.setAttribute('title', next ? 'Click to show less' : 'Click to read more');
        if (!next) {
          // Collapsing from far down the page — bring the intro back into view.
          var top = wrap.getBoundingClientRect().top;
          if (top < headerOffset()) {
            window.scrollTo({ top: window.scrollY + top - headerOffset() - 24, behavior: reduceMotion ? 'auto' : 'smooth' });
          }
        }
      }

      copy.addEventListener('click', function (e) {
        if (!expandable) return;
        // Links inside the copy still navigate; a text selection isn't a click.
        if (e.target.closest && e.target.closest('a')) return;
        var sel = window.getSelection && window.getSelection();
        if (sel && String(sel).length) return;
        setExpanded(!expanded);
      });
      copy.addEventListener('keydown', function (e) {
        if (!expandable) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setExpanded(!expanded);
        }
      });

      measure();
      // Re-measure once fonts settle and on resize (line count changes).
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
      var resizeT = null;
      window.addEventListener('resize', function () {
        clearTimeout(resizeT);
        resizeT = setTimeout(measure, 120);
      });
    }

    /* ── Shop the range → product toolbar ─────────────────────────────── */
    if (jump) {
      jump.addEventListener('click', function (e) {
        var id = (jump.getAttribute('href') || '').replace(/^#/, '');
        var body = id && document.getElementById(id);
        if (!body) return; // plain anchor fallback
        e.preventDefault();
        var page = wrap.closest('[data-collection-page]') || document;
        // Land on the toolbar (count / filter / sort) so it locks in beneath
        // the header with the first row of products directly below.
        var target = page.querySelector('[data-sticky-bar]') || body;
        var y = window.scrollY + target.getBoundingClientRect().top - headerOffset();
        window.scrollTo({ top: Math.max(0, y), behavior: reduceMotion ? 'auto' : 'smooth' });
        // Keep the URL hash + move focus for keyboard / AT users.
        if (history.replaceState) history.replaceState(null, '', '#' + id);
        if (!body.hasAttribute('tabindex')) body.setAttribute('tabindex', '-1');
        body.focus({ preventScroll: true });
      });
    }
  }

  /* ── Designer switcher (brand collections) ───────────────────────────
     ← / → walk the Designers A–Z when nothing else owns the keys (no
     field focused, no modifier, no open dialog). Hovering an arrow
     prefetches that page so the hop feels instant; clicking nudges the row
     the way we're going before the navigation lands. */
  /* ── Quick browse on category collections ────────────────────────────
     [data-quick-browse] is an empty pill shell; fill it from the store menu
     (snippets/side-drawer.liquid) already on the page. The first panel that
     links to this collection is "the relevant place in the menu": its
     collection links (menu order, de-duplicated, minus the "Shop all" foot
     link) are the neighbours, wrapping at both ends. On a section's own top
     page (e.g. /collections/menswear) the arrows lead to its last / first
     child. The middle link opens that panel (data-menu-panel-open,
     drawers.js), falling back to the "Shop all" link. Returns false — pill
     stays hidden — when the collection isn't in the menu. */
  function pathOf(a) {
    return (a.pathname || '').replace(/\/+$/, '').toLowerCase();
  }

  function buildQuickBrowse(nav) {
    var drawer = document.querySelector('[data-side-drawer]');
    if (!drawer) return false;
    var here = window.location.pathname.replace(/\/+$/, '').toLowerCase();
    var panels = drawer.querySelectorAll('[data-mm-panel]');
    var match = null;

    for (var i = 0; i < panels.length && !match; i++) {
      var panel = panels[i];
      var key = panel.getAttribute('data-mm-panel');
      var all = panel.querySelector('.store-menu__all-link');
      var trigger = drawer.querySelector('[data-mm-trigger="' + key + '"]');
      var seen = {};
      var items = [];
      panel.querySelectorAll('a[href*="/collections/"]').forEach(function (a) {
        if (a.closest('.store-menu__all')) return;
        var p = pathOf(a);
        if (!p || seen[p]) return;
        seen[p] = true;
        items.push({ path: p, href: a.getAttribute('href'), title: (a.textContent || '').trim() });
      });
      var idx = -1;
      items.forEach(function (it, n) { if (it.path === here) idx = n; });
      var isTop = idx < 0 && ((all && pathOf(all) === here) || (trigger && pathOf(trigger) === here));
      if (idx >= 0 || isTop) {
        match = { key: key, items: items, idx: idx, label: panel.getAttribute('aria-label') || '', all: all };
      }
    }
    if (!match || match.items.length < 2) return false;

    var n = match.items.length;
    var prev = match.idx < 0 ? match.items[n - 1] : match.items[(match.idx - 1 + n) % n];
    var next = match.idx < 0 ? match.items[0] : match.items[(match.idx + 1) % n];

    function arrow(d) {
      var span = document.createElement('span');
      span.className = 'designer-nav__btn';
      span.setAttribute('aria-hidden', 'true');
      span.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"><path d="' + d + '"/></svg>';
      return span;
    }
    function link(item, dir) {
      var a = document.createElement('a');
      a.className = 'designer-nav__link designer-nav__link--' + dir;
      a.href = item.href;
      a.setAttribute(dir === 'prev' ? 'data-designer-nav-prev' : 'data-designer-nav-next', '');
      a.setAttribute('aria-label', (dir === 'prev' ? 'Previous: ' : 'Next: ') + item.title);
      var name = document.createElement('span');
      name.className = 'designer-nav__name';
      name.textContent = item.title;
      var btn = arrow(dir === 'prev' ? 'M19 12H5m6-6-6 6 6 6' : 'M5 12h14m-6-6 6 6-6 6');
      if (dir === 'prev') { a.appendChild(btn); a.appendChild(name); }
      else { a.appendChild(name); a.appendChild(btn); }
      return a;
    }

    var mid = document.createElement('a');
    mid.className = 'designer-nav__all';
    mid.href = match.all ? match.all.getAttribute('href') : match.items[0].href;
    mid.setAttribute('data-menu-panel-open', match.key);
    mid.setAttribute('aria-haspopup', 'dialog');
    mid.textContent = 'Quick browse all ' + match.label;

    nav.setAttribute('aria-label', 'Quick browse ' + match.label);
    nav.appendChild(link(prev, 'prev'));
    nav.appendChild(mid);
    nav.appendChild(link(next, 'next'));
    nav.hidden = false;
    return true;
  }

  function initDesignerNav(nav) {
    if (nav.hasAttribute('data-designer-nav-ready')) return;
    if (nav.hasAttribute('data-quick-browse') && !nav.children.length && !buildQuickBrowse(nav)) return;
    nav.setAttribute('data-designer-nav-ready', '');
    var prev = nav.querySelector('[data-designer-nav-prev]');
    var next = nav.querySelector('[data-designer-nav-next]');
    if (!prev || !next) return;

    var prefetched = {};
    function prefetch(href) {
      if (!href || prefetched[href] || !('createElement' in document)) return;
      prefetched[href] = true;
      var l = document.createElement('link');
      l.rel = 'prefetch';
      l.href = href;
      l.as = 'document';
      document.head.appendChild(l);
    }

    function leave(link, dir) {
      nav.classList.add(dir === 'prev' ? 'is-leaving-prev' : 'is-leaving-next');
    }

    [prev, next].forEach(function (link) {
      var dir = link === prev ? 'prev' : 'next';
      link.addEventListener('pointerenter', function () { prefetch(link.href); });
      link.addEventListener('focus', function () { prefetch(link.href); });
      link.addEventListener('click', function (e) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        leave(link, dir);
      });
    });

    // Only show once the reader has scrolled down (~40% of a screen); hide
    // again back at the top. rAF-throttled, passive.
    var ticking = false;
    function reveal() {
      ticking = false;
      nav.classList.toggle('is-shown', window.scrollY > window.innerHeight * 0.4);
    }
    reveal();
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(reveal);
    }, { passive: true });

    // The pill is fixed bottom-centre; the tailor note is fixed bottom-left.
    // When the note is showing and the two share horizontal space, stack the
    // pill just above it (--dsw-lift); otherwise sit flush at the gap.
    var note = document.querySelector('[data-tailor-note]');
    function place() {
      var lift = 0;
      if (note && note.classList.contains('tailor-note--visible')) {
        var a = note.getBoundingClientRect();
        var b = nav.getBoundingClientRect();
        if (a.width && a.right + 12 > b.left && b.right + 12 > a.left) lift = note.offsetHeight + 12;
      }
      nav.style.setProperty('--dsw-lift', lift + 'px');
    }
    if (note) {
      place();
      new MutationObserver(place).observe(note, { attributes: true, attributeFilter: ['class'] });
      window.addEventListener('resize', place, { passive: true });
    }

    function keysFree() {
      var a = document.activeElement;
      if (a && a !== document.body && a !== document.documentElement) {
        var tag = a.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || a.isContentEditable) return false;
        if (a.closest && a.closest('[role="dialog"], [aria-modal="true"], dialog')) return false;
      }
      if (document.querySelector('dialog[open], [aria-modal="true"]:not([hidden]), .is-open[data-drawer], body.drawer-open, body.is-drawer-open')) return false;
      return true;
    }

    document.addEventListener('keydown', function (e) {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (!keysFree()) return;
      var link = e.key === 'ArrowLeft' ? prev : next;
      e.preventDefault();
      leave(link, e.key === 'ArrowLeft' ? 'prev' : 'next');
      window.location.href = link.href;
    });
  }

  function init() {
    document.querySelectorAll('[data-collection-intro]').forEach(initIntro);
    document.querySelectorAll('[data-designer-nav]').forEach(initDesignerNav);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Section re-render in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
